import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api, fmtMoney } from '../lib/api.js';

export default function Contractors() {
  const { isOwner } = useOutletContext();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // contractor obj or {} for new
  const [err, setErr] = useState('');

  async function load() {
    try { setData(await api.get('/api/contractors')); } catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  if (!isOwner) return <div className="text-rust-700 text-sm">Owner only.</div>;
  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!data) return <div className="text-ink-700/60 italic">Loading…</div>;

  const threshold = 60000;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-serif text-olive-900">Contractors</h1>
        <button onClick={() => setEditing({})} className="btn-primary">+ Add</button>
      </header>

      <p className="text-sm text-ink-700/70">
        Hired-labor records. YTD column flags anyone over <strong>{fmtMoney(threshold)}</strong> for 1099-NEC issuance ({data.year}).
      </p>

      {data.contractors.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-ink-700/70">No contractors yet.</p>
          <button onClick={() => setEditing({})} className="btn-primary mt-4">Add first contractor</button>
        </div>
      ) : (
        <ul className="space-y-2">
          {data.contractors.map(c => (
            <li key={c.id} className="card">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <div className="font-medium text-ink-900">{c.name}</div>
                  <div className="text-xs text-ink-700/60 mt-1 flex flex-wrap gap-x-3">
                    {!!c.phone && <span>{c.phone}</span>}
                    {c.has_ein
                      ? <span className="pill bg-olive-100 text-olive-900">EIN/SSN ✓</span>
                      : <span className="pill bg-rust-500/10 text-rust-700">EIN/SSN missing</span>}
                    {c.w9_on_file
                      ? <span className="pill bg-olive-100 text-olive-900">W-9 ✓</span>
                      : <span className="pill bg-rust-500/10 text-rust-700">W-9 missing</span>}
                    {!!c.default_day_rate_cents && <span>Day rate: {fmtMoney(c.default_day_rate_cents)}</span>}
                  </div>
                  {c.notes && <div className="text-xs text-ink-700/60 mt-1 italic">{c.notes}</div>}
                </div>
                <div className="text-right">
                  <div className="text-sm">YTD: <strong className={c.ytd_paid_cents >= threshold ? 'text-rust-700' : 'text-olive-900'}>{fmtMoney(c.ytd_paid_cents)}</strong></div>
                  {c.ytd_paid_cents >= threshold && <div className="pill bg-rust-500/15 text-rust-700 mt-1">FILE 1099-NEC</div>}
                  <button onClick={() => setEditing(c)} className="text-xs text-olive-700 hover:underline mt-2 block">edit</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <ContractorForm
          contractor={editing.id ? editing : null}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ContractorForm({ contractor, onClose }) {
  const [form, setForm] = useState({
    name: contractor?.name || '',
    phone: contractor?.phone || '',
    address: contractor?.address || '',
    ein_or_ssn: '',
    w9_on_file: !!contractor?.w9_on_file,
    default_day_rate_dollars: contractor?.default_day_rate_cents != null ? (contractor.default_day_rate_cents / 100).toString() : '',
    notes: contractor?.notes || '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const body = {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        ein_or_ssn: form.ein_or_ssn.trim() || (contractor ? undefined : null),
        w9_on_file: form.w9_on_file,
        default_day_rate_cents: form.default_day_rate_dollars ? Math.round(Number(form.default_day_rate_dollars) * 100) : null,
        notes: form.notes.trim() || null,
      };
      if (contractor?.id) await api.patch(`/api/contractors/${contractor.id}`, body);
      else await api.post('/api/contractors', body);
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-ink-900/40 flex items-center justify-center p-4 z-30" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-cream-50 rounded-2xl p-6 max-w-lg w-full space-y-3 shadow-xl">
        <h2 className="text-xl font-serif text-olive-900">{contractor ? 'Edit contractor' : 'Add contractor'}</h2>
        <div>
          <label className="label">Name *</label>
          <input className="input" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Joe Hire" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Phone</label>
            <input className="input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(601) 555-0123" />
          </div>
          <div>
            <label className="label">Default day rate ($)</label>
            <input className="input" type="number" step="0.01" value={form.default_day_rate_dollars} onChange={e => set('default_day_rate_dollars', e.target.value)} placeholder="200" />
          </div>
        </div>
        <div>
          <label className="label">Address</label>
          <input className="input" value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Farm Rd, Senatobia, MS" />
        </div>
        <div>
          <label className="label">EIN or SSN {contractor ? '(leave blank to keep current)' : ''}</label>
          <input className="input font-mono" value={form.ein_or_ssn} onChange={e => set('ein_or_ssn', e.target.value)} placeholder="XX-XXXXXXX or XXX-XX-XXXX" />
          <p className="text-xs text-ink-700/60 mt-1">Encrypted at rest with AES-256-GCM. Required for 1099-NEC at year-end.</p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.w9_on_file} onChange={e => set('w9_on_file', e.target.checked)} />
          W-9 on file
        </label>
        <div>
          <label className="label">Notes</label>
          <textarea className="input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
        {err && <div className="text-rust-700 text-sm">{err}</div>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}
